/**
 * Image fit and box math for Image layers.
 *
 * The image renderer, the canvas box helpers, and the toolbar W/H inputs
 * all use these functions to agree on one thing: how big an image's box
 * is, and how the image itself should scale and sit inside that box.
 *
 * It also counts each time a renderer places a media child, for animations
 * that start from that placement (Ken Burns).
 */

import { FIT_OPTIONS } from '@/constants/media';
import type { FitOptions } from '@/types/layers/media';
import type { ImageBox, ImageFitResult } from '@/types/canvas/imageFit';

/** How many times a renderer has placed each media child. */
const fitCounts = new WeakMap<object, number>();

/**
 * Note that a renderer has just placed a media child for its current file,
 * box and fit mode.
 */
export function markMediaChildFitted(child: object): void {
  fitCounts.set(child, (fitCounts.get(child) ?? 0) + 1);
}

/**
 * Changes every time a renderer places the child again: a new file (a feed
 * row, a URL swap), a new box or a new fit mode.
 */
export function mediaChildFitCount(child: object): number {
  return fitCounts.get(child) ?? 0;
}

/**
 * Resolve the container box dimensions for an Image layer.
 *
 * The image renderer and toolbar both treat the box as the source of
 * truth for the visible footprint. When `fitBoxWidth/Height` is set,
 * use it directly. Otherwise derive the box from the natural image
 * size times the layer scale, so a layer without an explicit box still
 * renders at the correct footprint.
 */
export function resolveImageBox(
  imageWidth: number,
  imageHeight: number,
  props: {
    fitBoxWidth?: number;
    fitBoxHeight?: number;
    scaleX?: number;
    scaleY?: number;
  }
): ImageBox {
  const fallbackWidth = imageWidth * Math.abs(props.scaleX ?? 1);
  const fallbackHeight = imageHeight * Math.abs(props.scaleY ?? 1);

  return {
    width: Math.max(1, props.fitBoxWidth ?? fallbackWidth),
    height: Math.max(1, props.fitBoxHeight ?? fallbackHeight)
  };
}

/**
 * Fit a source's natural size inside an ad, preserving the source's
 * aspect ratio and never upscaling. This is the box a freshly dropped
 * media item gets on each ad, and the box ORIGINAL resets to.
 */
export function computeFittedBox(
  srcWidth: number,
  srcHeight: number,
  adWidth: number,
  adHeight: number
): ImageBox {
  if (!srcWidth || !srcHeight) {
    return { width: Math.max(1, adWidth), height: Math.max(1, adHeight) };
  }

  const scale = Math.min(adWidth / srcWidth, adHeight / srcHeight, 1);

  return {
    width: Math.max(1, srcWidth * scale),
    height: Math.max(1, srcHeight * scale)
  };
}

/**
 * Compute the transform for the inner image given a fit mode.
 *
 *   - STRETCH: scale each axis on its own so the image fills the box
 *     exactly. Distorts aspect ratio.
 *   - FIT: scale uniformly so the image fits inside the box.
 *     Preserves aspect ratio; box may show empty space.
 *   - FILL: scale uniformly so the image covers the box. Preserves
 *     aspect ratio; container clipPath crops the overflow.
 *   - ORIGINAL: renders identically to STRETCH. The box is reset to the
 *     source's fitted size when the mode is chosen or the source is
 *     replaced, so ORIGINAL only differs from STRETCH in that box-reset
 *     event, not at draw time.
 */
export function computeImageFit(
  fit: FitOptions | undefined,
  imageWidth: number,
  imageHeight: number,
  boxWidth: number,
  boxHeight: number
): ImageFitResult {
  if (!imageWidth || !imageHeight || !boxWidth || !boxHeight) {
    return { scaleX: 1, scaleY: 1, left: 0, top: 0 };
  }

  const mode = fit ?? FIT_OPTIONS.STRETCH;

  switch (mode) {
    case FIT_OPTIONS.ORIGINAL:
    case FIT_OPTIONS.STRETCH: {
      return {
        scaleX: boxWidth / imageWidth,
        scaleY: boxHeight / imageHeight,
        left: 0,
        top: 0
      };
    }
    case FIT_OPTIONS.FILL: {
      const scale = Math.max(boxWidth / imageWidth, boxHeight / imageHeight);
      return { scaleX: scale, scaleY: scale, left: 0, top: 0 };
    }
    default: {
      const scale = Math.min(boxWidth / imageWidth, boxHeight / imageHeight);
      return { scaleX: scale, scaleY: scale, left: 0, top: 0 };
    }
  }
}

/**
 * Read the rendered width/height of a Fabric object for the toolbar
 * W/H inputs.
 *
 * Image container Groups store the box on `width/height` and keep
 * scale pinned to 1, so reading `width * scaleX` gives the box. When
 * the object does not expose a meaningful intrinsic size, fall back to
 * Fabric's `getScaledWidth/Height` so other shapes still work.
 */
export function getRenderedBox(obj: {
  width?: number;
  height?: number;
  scaleX?: number;
  scaleY?: number;
  getScaledWidth: () => number;
  getScaledHeight: () => number;
}): ImageBox {
  const width =
    obj.width && obj.width > 0
      ? obj.width * Math.abs(obj.scaleX || 1)
      : obj.getScaledWidth();
  const height =
    obj.height && obj.height > 0
      ? obj.height * Math.abs(obj.scaleY || 1)
      : obj.getScaledHeight();

  return {
    width: Math.max(1, width),
    height: Math.max(1, height)
  };
}
